// Libro mayor de tarjetas PCF de una tienda: qué pasó, cuánto y por qué, en orden de ocurrencia.
import { Button, CopyableId, EmptyState, ErrorState, Loading } from '@/kit';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import type { CardStockMovementResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockMovementResponse';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { userMessageOf } from '@/api/errors';
import { StatusBadge } from '../../components';
import { cardMovementReason } from '../cardMovementReason';
import { CARD_MOVEMENT_TYPES } from '../constants';

/** Un asiento repetido entre páginas se descarta: `movementSk` es único en la partición. */
export function dedupeBySk(movements: CardStockMovementResponse[]): CardStockMovementResponse[] {
    const seen = new Set<string>();
    return movements.filter((m) => {
        if (seen.has(m.movementSk)) return false;
        seen.add(m.movementSk);
        return true;
    });
}

export function CardMovementsTable({
    movements,
    isLoading,
    error,
    onRetry,
    hasMore,
    loadingMore,
    onLoadMore,
}: {
    movements: CardStockMovementResponse[];
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
}) {
    if (error) {
        return (
            <ErrorState
                title="No se pudo leer el inventario"
                description={userMessageOf(error)}
                onRetry={onRetry}
            />
        );
    }
    if (isLoading) return <Loading text="Cargando inventario…" />;
    if (movements.length === 0) {
        return (
            <EmptyState
                title="Sin movimientos"
                description="Esta tienda todavía no registra entradas ni salidas de tarjetas. Registra un lote para empezar."
            />
        );
    }

    return (
        <div className="space-y-2">
            <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Movimiento</TableHead>
                            <TableHead className="text-right">Cantidad</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Registró</TableHead>
                            <TableHead>Detalle</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {movements.map((m) => (
                            <TableRow key={m.movementSk}>
                                <TableCell>
                                    <StatusBadge value={m.type} config={CARD_MOVEMENT_TYPES} />
                                </TableCell>
                                <TableCell className="text-right font-mono text-[12px] tabular-nums">
                                    {m.quantity > 0 ? `+${String(m.quantity)}` : String(m.quantity)}
                                </TableCell>
                                <TableCell className="text-[12px]">
                                    {formatDateTimeMx(m.occurredAt)}
                                </TableCell>
                                <TableCell className="text-[12px] text-muted-foreground">
                                    {/* `actorUserId` trae el email del operador, no un UUID. */}
                                    {/* eslint-disable-next-line no-restricted-syntax */}
                                    {m.actorUserId}
                                </TableCell>
                                <TableCell className="text-[12px] text-muted-foreground">
                                    {m.customerId ? (
                                        <CopyableId label="Cliente" value={m.customerId} />
                                    ) : (
                                        cardMovementReason(m)
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {hasMore ? (
                <div className="flex justify-center">
                    <Button
                        variant="outline"
                        size="sm"
                        isLoading={loadingMore}
                        onClick={onLoadMore}
                    >
                        Ver más movimientos
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
